if (typeof VoiceMessageWidget !== 'function') {

	class VoiceMessageWidget extends HTMLElement {

		constructor() {
			super();
			this.onPlayClick = this.onPlayClick.bind(this);
			this.onTimeUpdate = this.onTimeUpdate.bind(this);
			this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
			this.onEnded = this.onEnded.bind(this);
			this.onPointerDown = this.onPointerDown.bind(this);
			this.onPointerMove = this.onPointerMove.bind(this);
			this.onPointerUp = this.onPointerUp.bind(this);
			this.onWaveformKeyDown = this.onWaveformKeyDown.bind(this);
			this._seeking = false;
			this._raf = 0;
		}

		connectedCallback() {
			this.playButton = this.querySelector('[data-voice-play]');
			this.waveform = this.querySelector('[data-voice-waveform]');
			this.timeEl = this.querySelector('[data-voice-time]');
			this.audio = this.querySelector('[data-voice-audio]');

			this.renderBars(VoiceMessageWidget.FALLBACK_BARS);

			if (this.playButton) {
				this.playButton.addEventListener('click', this.onPlayClick);
			}
			if (this.waveform) {
				this.waveform.addEventListener('pointerdown', this.onPointerDown);
				this.waveform.addEventListener('keydown', this.onWaveformKeyDown);
			}
			if (this.audio) {
				this.audio.addEventListener('timeupdate', this.onTimeUpdate);
				this.audio.addEventListener('loadedmetadata', this.onLoadedMetadata);
				this.audio.addEventListener('ended', this.onEnded);
				this.audio.addEventListener('play', this.syncPlayingState.bind(this, true));
				this.audio.addEventListener('pause', this.syncPlayingState.bind(this, false));
				if (this.audio.readyState >= 1) this.onLoadedMetadata();
				this.decodeWaveform(this.audio.src);
			}
		}

		disconnectedCallback() {
			if (this.playButton) {
				this.playButton.removeEventListener('click', this.onPlayClick);
			}
			if (this.waveform) {
				this.waveform.removeEventListener('pointerdown', this.onPointerDown);
				this.waveform.removeEventListener('keydown', this.onWaveformKeyDown);
			}
			if (this.audio) {
				this.audio.pause();
				this.audio.removeEventListener('timeupdate', this.onTimeUpdate);
				this.audio.removeEventListener('loadedmetadata', this.onLoadedMetadata);
				this.audio.removeEventListener('ended', this.onEnded);
			}
			this.stopProgressLoop();
			this.removePointerListeners();
		}

		get playLabel() {
			return this.dataset.playLabel || 'Play';
		}

		get pauseLabel() {
			return this.dataset.pauseLabel || 'Pause';
		}

		get duration() {
			if (!this.audio) return 0;
			const value = this.audio.duration;
			if (!value || !isFinite(value)) return 0;
			return value;
		}

		renderBars(heights) {
			if (!this.waveform) return;
			this.waveform.replaceChildren();

			const track = document.createElement('div');
			track.className = 'voice-message-widget__bars';
			const played = document.createElement('div');
			played.className = 'voice-message-widget__bars voice-message-widget__bars--played';

			heights.forEach(function(height) {
				const value = String(Math.max(0.18, Math.min(1, height)));
				track.appendChild(VoiceMessageWidget.createBar(value));
				played.appendChild(VoiceMessageWidget.createBar(value));
			});

			this.waveform.appendChild(track);
			this.waveform.appendChild(played);
			this.updateProgress(this.audio ? this.audio.currentTime : 0);
		}

		decodeWaveform(src) {
			if (!src || typeof fetch !== 'function' || typeof AudioContext === 'undefined' && typeof webkitAudioContext === 'undefined') return;
			const widget = this;
			fetch(src).then(function(response) {
				if (!response.ok) throw new Error('fetch failed');
				return response.arrayBuffer();
			}).then(function(buffer) {
				const Context = window.AudioContext || window.webkitAudioContext;
				const context = new Context();
				return context.decodeAudioData(buffer).then(function(decoded) {
					if (typeof context.close === 'function') context.close();
					widget.renderBars(widget.computeHeights(decoded));
				});
			}).catch(function() {});
		}

		computeHeights(buffer) {
			const data = buffer.getChannelData(0);
			const count = VoiceMessageWidget.BAR_COUNT;
			const block = Math.max(1, Math.floor(data.length / count));
			const heights = [];
			let max = 0.01;
			for (let i = 0; i < count; i += 1) {
				const start = i * block;
				let sum = 0;
				let samples = 0;
				for (let j = 0; j < block; j += 24) {
					sum += Math.abs(data[start + j] || 0);
					samples += 1;
				}
				const rms = samples ? sum / samples : 0;
				if (rms > max) max = rms;
				heights.push(rms);
			}
			return heights.map(function(value) {
				return 0.2 + 0.8 * (value / max);
			});
		}

		onPlayClick(event) {
			event.preventDefault();
			if (!this.audio || !this.audio.src) return;
			if (this.audio.paused) {
				this.pauseOthers();
				this.trackClick();
				this.audio.play().catch(function() {});
			} else {
				this.audio.pause();
			}
		}

		get trackingKey() {
			return this.dataset.productId || this.dataset.productHandle || this.id || 'voice-message';
		}

		hasTrackedClick() {
			try {
				const stored = localStorage.getItem(VoiceMessageWidget.TRACKING_STORAGE_KEY);
				const tracked = stored ? JSON.parse(stored) : [];
				return Array.isArray(tracked) && tracked.indexOf(this.trackingKey) !== -1;
			} catch (err) {
				return false;
			}
		}

		markClickTracked() {
			try {
				const stored = localStorage.getItem(VoiceMessageWidget.TRACKING_STORAGE_KEY);
				const tracked = stored ? JSON.parse(stored) : [];
				if (!Array.isArray(tracked)) return;
				if (tracked.indexOf(this.trackingKey) === -1) {
					tracked.push(this.trackingKey);
					localStorage.setItem(VoiceMessageWidget.TRACKING_STORAGE_KEY, JSON.stringify(tracked));
				}
			} catch (err) {}
		}

		trackClick() {
			if (this.hasTrackedClick()) return;
			this.markClickTracked();
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'voice_message_click',
				product_handle: this.dataset.productHandle || undefined,
				product_id: this.dataset.productId || undefined,
				widget_id: this.id || undefined,
				voice_message_action: 'play'
			});
		}

		pauseOthers() {
			document.querySelectorAll('voice-message-widget').forEach(function(widget) {
				if (widget === this) return;
				const audio = widget.querySelector('[data-voice-audio]');
				if (audio && !audio.paused) audio.pause();
			}, this);
		}

		syncPlayingState(playing) {
			this.classList.toggle('is-playing', playing);
			if (playing) this.startProgressLoop();
			else this.stopProgressLoop();
			if (!this.playButton) return;
			this.playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
			this.playButton.setAttribute('aria-label', playing ? this.pauseLabel : this.playLabel);
		}

		startProgressLoop() {
			if (this._raf) return;
			const widget = this;
			const tick = function() {
				if (!widget.audio || widget.audio.paused) {
					widget._raf = 0;
					return;
				}
				if (!widget._seeking) widget.updateProgress(widget.audio.currentTime);
				widget._raf = requestAnimationFrame(tick);
			};
			this._raf = requestAnimationFrame(tick);
		}

		stopProgressLoop() {
			if (this._raf) cancelAnimationFrame(this._raf);
			this._raf = 0;
		}

		onLoadedMetadata() {
			this.updateTimeDisplay(this.duration);
			this.updateProgress(this.audio ? this.audio.currentTime : 0);
		}

		onTimeUpdate() {
			if (this._seeking) return;
			this.updateProgress(this.audio.currentTime);
		}

		onEnded() {
			this.audio.currentTime = 0;
			this.updateProgress(0);
			this.syncPlayingState(false);
		}

		updateProgress(currentTime) {
			const duration = this.duration;
			const remaining = duration > 0 ? Math.max(0, duration - currentTime) : 0;
			this.updateTimeDisplay(duration > 0 ? remaining : 0);
			const ratio = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0;
			if (this.waveform) {
				this.waveform.style.setProperty('--progress', String(ratio));
				this.waveform.setAttribute('aria-valuemax', String(Math.round(duration)));
				this.waveform.setAttribute('aria-valuenow', String(Math.round(currentTime)));
			}
		}

		updateTimeDisplay(seconds) {
			if (!this.timeEl) return;
			this.timeEl.textContent = VoiceMessageWidget.formatTime(seconds);
		}

		seekFromEvent(event) {
			if (!this.audio || !this.waveform || this.duration <= 0) return;
			const rect = this.waveform.getBoundingClientRect();
			const ratio = rect.width ? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) : 0;
			this.audio.currentTime = ratio * this.duration;
			this.updateProgress(this.audio.currentTime);
		}

		onPointerDown(event) {
			if (!this.audio || this.duration <= 0) return;
			this._seeking = true;
			this.waveform.setPointerCapture(event.pointerId);
			this.seekFromEvent(event);
			window.addEventListener('pointermove', this.onPointerMove);
			window.addEventListener('pointerup', this.onPointerUp);
			window.addEventListener('pointercancel', this.onPointerUp);
		}

		onPointerMove(event) {
			if (!this._seeking) return;
			this.seekFromEvent(event);
		}

		onPointerUp(event) {
			if (this._seeking && this.waveform && event && event.pointerId != null) {
				try { this.waveform.releasePointerCapture(event.pointerId); } catch (err) {}
			}
			this._seeking = false;
			this.removePointerListeners();
		}

		removePointerListeners() {
			window.removeEventListener('pointermove', this.onPointerMove);
			window.removeEventListener('pointerup', this.onPointerUp);
			window.removeEventListener('pointercancel', this.onPointerUp);
		}

		onWaveformKeyDown(event) {
			if (!this.audio || this.duration <= 0) return;
			let delta = 0;
			if (event.key === 'ArrowRight') delta = 5;
			if (event.key === 'ArrowLeft') delta = -5;
			if (!delta) return;
			event.preventDefault();
			this.audio.currentTime = Math.max(0, Math.min(this.duration, this.audio.currentTime + delta));
			this.updateProgress(this.audio.currentTime);
		}

		static createBar(height) {
			const bar = document.createElement('span');
			bar.className = 'voice-message-widget__bar';
			bar.setAttribute('aria-hidden', 'true');
			bar.style.setProperty('--bar-h', height);
			return bar;
		}

		static formatTime(seconds) {
			const total = Math.max(0, Math.floor(seconds || 0));
			const minutes = Math.floor(total / 60);
			const rest = total % 60;
			return minutes + ':' + String(rest).padStart(2, '0');
		}

	}

	VoiceMessageWidget.TRACKING_STORAGE_KEY = 'voice_message_click_tracked';
	VoiceMessageWidget.BAR_COUNT = 40;
	VoiceMessageWidget.FALLBACK_BARS = [
		0.28, 0.42, 0.35, 0.55, 0.48, 0.72, 0.62, 0.88, 0.7, 0.52,
		0.4, 0.65, 0.8, 0.58, 0.45, 0.9, 0.75, 0.5, 0.38, 0.6,
		0.85, 0.68, 0.44, 0.32, 0.5, 0.78, 0.92, 0.6, 0.42, 0.55,
		0.7, 0.48, 0.36, 0.58, 0.8, 0.64, 0.4, 0.3, 0.45, 0.38
	];

	customElements.define('voice-message-widget', VoiceMessageWidget);
}
